import { effect, inject, Injectable, signal } from '@angular/core';
import { AldermanshipDto } from '@shared/interfaces/dtos/aldermanships';
import { AldermanshipsService } from '@shared/services/aldermanships.service';
import { catchError, forkJoin, map, of } from 'rxjs';
import { DecisionTypesService } from '@shared/services/decision-types.service';
import { DecisionTypeDto } from '@shared/interfaces/dtos/decision-types';
import { UsersService } from '@shared/services/users.service';
import { UserDto } from '@shared/interfaces/dtos/users';
import { ServicesService } from '@shared/services/services.service';
import { ServiceDto } from '@shared/interfaces/dtos/services';
import { GroupsService } from '@shared/services/groups.service';
import { GroupDto } from '@shared/interfaces/dtos/groups';
import { ActionKindDto } from '@shared/interfaces/dtos/actions';
import { ActionsService } from '@shared/services/actions.service';
import { SsoService } from '@devinforius/i-sso';
import { ADDRESS_TYPES, GENDERS, NATIONALITIES, ONE_HOUR_IN_MS } from '@shared/consts';
import { Logger } from 'src/app/core/services';
import { DropdownOption } from '@shared/interfaces/dev';
import { Gender } from '@shared/enums';
import { ContactTypeDto } from '@shared/interfaces/dtos/contacts';
import { ContactsService } from '@shared/services/contacts.service';
import { AddressType } from '@shared/types/addresses/address-type.type';
import { AddressesService } from '@shared/services/addresses.service';
import { CountryDto } from '@shared/interfaces/dtos/addresses';

@Injectable({ providedIn: 'root' })
export class FormOptions {
  private readonly sso = inject(SsoService);
  private readonly logger = inject(Logger);

  private readonly aldermanshipsService = inject(AldermanshipsService);
  private readonly decisionTypesService = inject(DecisionTypesService);
  private readonly usersService = inject(UsersService);
  private readonly servicesService = inject(ServicesService);
  private readonly groupsService = inject(GroupsService);
  private readonly actionsService = inject(ActionsService);
  private readonly contactsService = inject(ContactsService);
  private readonly addressesService = inject(AddressesService);

  private readonly _optionsLoaded = signal<boolean>(false);
  public readonly optionsLoaded = this._optionsLoaded.asReadonly();

  private readonly aldermanshipsOptions = signal<AldermanshipDto[]>([]);
  public readonly aldermanships = this.aldermanshipsOptions.asReadonly();

  private readonly decisionTypesOptions = signal<DecisionTypeDto[]>([]);
  public readonly decisionTypes = this.decisionTypesOptions.asReadonly();

  private readonly usersOptions = signal<UserDto[]>([]);
  public readonly users = this.usersOptions.asReadonly();

  private readonly contactTypesOptions = signal<ContactTypeDto[]>([]);
  public readonly contactTypes = this.contactTypesOptions.asReadonly();

  private readonly servicesOptions = signal<ServiceDto[]>([]);
  public readonly services = this.servicesOptions.asReadonly();

  private readonly groupsOptions = signal<GroupDto[]>([]);
  public readonly groups = this.groupsOptions.asReadonly();

  private readonly actionKindsOptions = signal<ActionKindDto[]>([]);
  public readonly actionKinds = this.actionKindsOptions.asReadonly();

  private readonly gendersOptions = signal<DropdownOption<Gender>[]>(GENDERS);
  public readonly genders = this.gendersOptions.asReadonly();

  private readonly nationalitiesOptions = signal<DropdownOption<string>[]>(NATIONALITIES);
  public readonly nationalities = this.nationalitiesOptions.asReadonly();

  private readonly addressTypeOptions = signal<DropdownOption<AddressType>[]>(ADDRESS_TYPES);
  public readonly addresses = this.addressTypeOptions.asReadonly();

  private readonly countriesOptions = signal<DropdownOption<CountryDto>[]>([]);
  public readonly countries = this.countriesOptions.asReadonly();

  constructor() {
    effect(
      () => {
        const isConnected = this.sso.isConnected();

        if (isConnected) {
          this.initAllOptions();
        }
      },
      { allowSignalWrites: true }
    );
  }

  public init(): this {
    this._optionsLoaded.set(false);
    this.initAllOptions();

    return this;
  }

  private initAllOptions(): void {
    this.logger.log('Initializing global form options...', 'Form options instance');
    this.startCounterBeforeRefresh();
    forkJoin({
      aldermanships: this.aldermanshipsService.fetchAldermanships().pipe(catchError(() => of([] as AldermanshipDto[]))),
      decisionTypes: this.decisionTypesService.fetchDecisionTypes().pipe(catchError(() => of([] as DecisionTypeDto[]))),
      users: this.usersService.fetchUsers().pipe(
        map((res) =>
          res.map((user) => ({ ...user, nom: user.contactForUser.nom, prenom: user.contactForUser.prenom }))
        ),
        catchError(() => of([] as UserDto[]))
      ),
      services: this.servicesService.fetchServices().pipe(catchError(() => of([] as ServiceDto[]))),
      groups: this.groupsService.fetchGroups().pipe(catchError(() => of([] as GroupDto[]))),
      actionKinds: this.actionsService.fetchActionKinds().pipe(catchError(() => of([] as ActionKindDto[]))),
      contactTypes: this.contactsService.fetchContactTypes().pipe(catchError(() => of([] as ContactTypeDto[]))),
      countries: this.addressesService.fetchCountries().pipe(catchError(() => of([] as CountryDto[]))),
    }).subscribe({
      next: (options) => {
        const { aldermanships, decisionTypes, users, services, groups, actionKinds, contactTypes, countries } = options;

        this.aldermanshipsOptions.set(aldermanships);
        this.decisionTypesOptions.set(decisionTypes);
        this.usersOptions.set(users);
        this.servicesOptions.set(services);
        this.groupsOptions.set(groups);
        this.actionKindsOptions.set(actionKinds);
        this.contactTypesOptions.set(contactTypes);
        this.countriesOptions.set(countries.map((country) => ({ label: country.description, value: country })));

        this.logger.log('Global form options initialized.', 'Form options instance');

        this._optionsLoaded.set(true);
      },
    });
  }

  private startCounterBeforeRefresh(): void {
    setInterval(() => this.initAllOptions(), ONE_HOUR_IN_MS);
  }
}
